import { Link, createFileRoute, useRouterState } from '@tanstack/react-router'
import { DEFAULT_DATASET_PREVIEW_LIMIT } from '@contracts/datasets'
import {
  DataPreviewPage,
  parsePreviewSearch,
  type DatasetListSearch,
} from '@/features/model-training'

declare module '@tanstack/react-router' {
  interface HistoryState {
    /** List state of the Datasets step the user came from; Previous returns to it. */
    datasetList?: DatasetListSearch
  }
}

// `datasets_`: under the Datasets address without being rendered inside the Datasets page.
export const Route = createFileRoute('/app/model-training/datasets_/$datasetId/preview')({
  validateSearch: parsePreviewSearch,
  component: DataPreviewRoute,
})

function DataPreviewRoute() {
  const { datasetId } = Route.useParams()
  const search = Route.useSearch()
  const navigate = Route.useNavigate()
  // Absent after a reload or when the address was opened directly: the list then has its defaults.
  const listSearch = useRouterState({ select: (state) => state.location.state.datasetList }) ?? {}

  return (
    <DataPreviewPage
      datasetId={datasetId}
      limit={search.limit ?? DEFAULT_DATASET_PREVIEW_LIMIT}
      onLimitChange={(limit) => {
        void navigate({
          search: { limit: limit === DEFAULT_DATASET_PREVIEW_LIMIT ? undefined : limit },
          // Not a new history entry, and the list state travels on.
          replace: true,
          state: (previous) => previous,
        })
      }}
      onBackToDatasets={() => {
        void navigate({ to: '/app/model-training/datasets', search: listSearch })
      }}
      onNext={() => {
        void navigate({
          to: '/app/model-training/datasets/$datasetId/features',
          params: { datasetId },
          // The list state travels on, so Previous still returns to it after Feature Selection.
          state: { datasetList: listSearch },
        })
      }}
      renderDatasetsLink={(content, className) => (
        <Link to="/app/model-training/datasets" search={listSearch} className={className}>
          {content}
        </Link>
      )}
    />
  )
}
